<script lang="ts">
  import type { Emote } from '@pin/schemas/emotes';
  import { tick } from 'svelte';
  import EmoteGrid from '../EmoteGrid.svelte';
  import FaceSmiling from '../Icons/FaceSmiling.svelte';

  interface Props {
    /** The field the shortcode goes into, at its caret. */
    input: HTMLTextAreaElement | undefined;
    value: string;
    disabled?: boolean;
  }
  let { input, value = $bindable(), disabled = false }: Props = $props();

  let open = $state(false);
  let root = $state<HTMLDivElement>();

  const insert = async (emote: Emote) => {
    if (input == null) return;
    const [shortcode] = emote.shortcodes;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    // maxlength only stops typing, not a value set from here
    const length = value.length - (end - start) + shortcode.length;
    if (input.maxLength >= 0 && length > input.maxLength) return;
    value = value.slice(0, start) + shortcode + value.slice(end);
    await tick();
    // For the command and user popups, which follow the field's input event
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
    input.setSelectionRange(start + shortcode.length, start + shortcode.length);
  };
</script>

<svelte:document
  onclick={(e) => {
    if (!open || !(e.target instanceof Node)) return;
    // Clicking in the message moves the caret and keeps the picker open
    if (root?.contains(e.target) || input?.contains(e.target)) return;
    open = false;
  }}
  onkeydown={(e) => {
    if (open && e.key === 'Escape') {
      open = false;
      input?.focus();
    }
  }}
/>

<div class="emote-picker" bind:this={root}>
  {#if open}
    <div id="emote-picker" class="popup">
      <EmoteGrid onpick={insert} />
    </div>
  {/if}
  <button
    type="button"
    class="toggle"
    class:open
    title="Emotes"
    aria-label="Emotes"
    aria-expanded={open}
    aria-controls="emote-picker"
    {disabled}
    onclick={() => (open = !open)}
  >
    <FaceSmiling />
  </button>
</div>

<style>
  /* The button and the popup are placed against the positioned box around
     the field, so the popup is never wider than the field */
  .emote-picker {
    display: contents;
  }

  .toggle {
    all: unset;
    position: absolute;
    top: 50%;
    right: 0.5rem;
    transform: translateY(-50%);
    display: flex;
    color: var(--color-zinc-400);
    cursor: pointer;
  }

  .toggle:hover,
  .toggle.open {
    color: var(--color-zinc-200);
  }

  .toggle:focus-visible {
    outline: 1px solid var(--color-zinc-400);
    border-radius: 0.25rem;
  }

  .toggle:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .popup {
    position: absolute;
    right: 0;
    bottom: 100%;
    left: 0;
    max-height: 24rem;
    overflow-y: auto;
    padding: 0.5rem;
    background-color: var(--color-zinc-700);
    border: 1px solid var(--color-zinc-600);
    border-radius: 0.5rem;
    z-index: 10;
  }
</style>
